import { SocketMessageInterface, DoorState } from "../../../interfaces/Monitoring.interface";
import { Usage } from "../../../flatbuffers/generated/usage/usage";
import PositionCallsStatusT from "../../../types/PositionCallsStatus";

const MIN_MOCK_FLOOR = 1;

const normalizePositionCalls = (
  value?: Partial<PositionCallsStatusT> | null,
): PositionCallsStatusT =>
  new PositionCallsStatusT(
    Array.isArray(value?.front) ? value.front : [],
    Array.isArray(value?.rear) ? value.rear : [],
    Array.isArray(value?.frontLock) ? value.frontLock : [],
    Array.isArray(value?.rearLock) ? value.rearLock : [],
  );

export function anyDoorOpen(doorStatus?: Partial<DoorState> | null): boolean {
  if (!doorStatus) return false;
  return Boolean(
    doorStatus.frontOpen ||
    doorStatus.rearOpen ||
    doorStatus.frontStalled ||
    doorStatus.rearStalled,
  );
}

export function clampMockFloor(
  position: number | null | undefined,
  minFloor = MIN_MOCK_FLOOR,
  maxFloor = 10,
): number {
  if (typeof position !== "number" || Number.isNaN(position)) {
    return minFloor;
  }
  return Math.min(Math.max(Math.round(position), minFloor), maxFloor);
}

type MockStateMeta = {
  pauseTicks: number;
};

type MockStateCarrier = Partial<SocketMessageInterface> & {
  mockStateMeta?: MockStateMeta;
};

function readMockStateMeta(
  current: Partial<SocketMessageInterface> | null | undefined,
): MockStateMeta {
  const candidate = (current as MockStateCarrier | null | undefined)?.mockStateMeta;

  if (
    !candidate ||
    typeof candidate.pauseTicks !== "number" ||
    Number.isNaN(candidate.pauseTicks)
  ) {
    return { pauseTicks: 0 };
  }

  return {
    pauseTicks: Math.max(0, Math.floor(candidate.pauseTicks)),
  };
}

export function generateMockLiftState(
  current: Partial<SocketMessageInterface>,
  maxFloor = 10,
): SocketMessageInterface {
  const minFloor = MIN_MOCK_FLOOR;
  const floorCount = Math.max(minFloor, maxFloor);
  const currentPosition = clampMockFloor(current.position ?? minFloor, minFloor, floorCount);
  const currentMeta = readMockStateMeta(current);

  const nextState: SocketMessageInterface = {
    bankId: current.bankId ?? ({} as SocketMessageInterface["bankId"]),
    liftId: current.liftId ?? ({} as SocketMessageInterface["liftId"]),
    carCalls: normalizePositionCalls(current.carCalls),
    hallCalls: {
      up: normalizePositionCalls(current.hallCalls?.up),
      down: normalizePositionCalls(current.hallCalls?.down),
      upAssigned: normalizePositionCalls(current.hallCalls?.upAssigned),
      downAssigned: normalizePositionCalls(current.hallCalls?.downAssigned),
    },
    faults: current.faults ?? false,
    activeFaults: current.activeFaults ?? [],
    position: currentPosition,
    serviceMode: current.serviceMode ?? null,
    direction: current.direction ?? Usage.Stopped,
    doorStatus: {
      frontOpen: false,
      rearOpen: false,
      frontStalled: false,
      rearStalled: false,
      ...(current.doorStatus ?? {}),
    },
    doorZone: current.doorZone ?? null,
    levelling: current.levelling ?? false,
    escalatorControl: current.escalatorControl ?? null,
    connected: true,
    msg: current.msg ?? {
      contentType: 0 as any,
      messageType: 0 as any,
      content: null,
      active: false,
    },
    usage: current.usage ?? {},
    bitmask: current.bitmask ?? {},
    positionMap: current.positionMap ?? {},
    serviceMap: current.serviceMap ?? {},
    oneHotMap: current.oneHotMap ?? {},
    withheldCalls: current.withheldCalls ?? [],
    secured: current.secured ?? { front: [], rear: [] },
    overlaid: current.overlaid ?? [],
    ack: current.ack ?? { outcome: 0, verified: 0, elapsedMs: 0 },
  };

  const applyMockUsageState = (direction: Usage, doors: DoorState) => {
    const frontOpen = Boolean(doors.frontOpen || doors.frontStalled);
    const rearOpen = Boolean(doors.rearOpen || doors.rearStalled);
    const moving = direction === Usage.Up || direction === Usage.Down;

    nextState.usage = {
      ...(current.usage ?? {}),
      [Usage.Up]: direction === Usage.Up,
      [Usage.Down]: direction === Usage.Down,
      [Usage.Moving]: moving,
      [Usage.Stopped]: !moving,
      [Usage.FrontDoorOpened]: frontOpen,
      [Usage.FrontDoorClosed]: !frontOpen,
      [Usage.FrontDoorOpening]: false,
      [Usage.FrontDoorClosing]: false,
      [Usage.RearDoorOpened]: rearOpen,
      [Usage.RearDoorClosed]: !rearOpen,
      [Usage.RearDoorOpening]: false,
      [Usage.RearDoorClosing]: false,
    };
  };

  if (currentMeta.pauseTicks > 0) {
    const remainingPauseTicks = Math.max(0, currentMeta.pauseTicks - 1);
    const shouldKeepDoorsOpen = remainingPauseTicks > 0;

    nextState.doorStatus = {
      ...nextState.doorStatus,
      frontOpen: shouldKeepDoorsOpen,
      rearOpen: shouldKeepDoorsOpen,
      frontStalled: false,
      rearStalled: false,
    };
    nextState.direction = Usage.Stopped;
    nextState.position = currentPosition;
    (nextState as MockStateCarrier).mockStateMeta = {
      pauseTicks: remainingPauseTicks,
    };
    applyMockUsageState(nextState.direction, nextState.doorStatus);

    if (shouldKeepDoorsOpen) {
      return nextState;
    }

    // Once the final pause tick expires, clear the stale door-open state and
    // resume motion without immediately reopening the doors again.
    nextState.doorStatus = {
      frontOpen: false,
      rearOpen: false,
      frontStalled: false,
      rearStalled: false,
    };
    nextState.direction = current.direction ?? Usage.Up;
    (nextState as MockStateCarrier).mockStateMeta = { pauseTicks: 0 };
    applyMockUsageState(nextState.direction, nextState.doorStatus);
  }

  if (anyDoorOpen(current.doorStatus) && currentMeta.pauseTicks <= 0) {
    const pauseTicks = 3 + Math.floor(Math.random() * 3);
    nextState.doorStatus = {
      ...nextState.doorStatus,
      frontOpen: true,
      rearOpen: true,
      frontStalled: false,
      rearStalled: false,
    };
    nextState.direction = Usage.Stopped;
    nextState.position = currentPosition;
    (nextState as MockStateCarrier).mockStateMeta = {
      pauseTicks,
    };
    applyMockUsageState(nextState.direction, nextState.doorStatus);
    return nextState;
  }

  if (Math.random() < 0.12) {
    const pauseTicks = 3 + Math.floor(Math.random() * 3);
    nextState.doorStatus = {
      ...nextState.doorStatus,
      frontOpen: true,
      rearOpen: true,
      frontStalled: false,
      rearStalled: false,
    };
    nextState.direction = Usage.Stopped;
    nextState.position = currentPosition;
    (nextState as MockStateCarrier).mockStateMeta = {
      pauseTicks,
    };
    applyMockUsageState(nextState.direction, nextState.doorStatus);
    return nextState;
  }

  let nextDirection = current.direction ?? Usage.Up;
  if (nextDirection !== Usage.Up && nextDirection !== Usage.Down) {
    nextDirection = currentPosition >= floorCount ? Usage.Down : Usage.Up;
  }

  if (nextDirection === Usage.Up && currentPosition >= floorCount) {
    nextDirection = Usage.Down;
  }
  if (nextDirection === Usage.Down && currentPosition <= minFloor) {
    nextDirection = Usage.Up;
  }

  let nextPosition = currentPosition;
  if (nextDirection === Usage.Up) {
    nextPosition = currentPosition + 1;
  } else {
    nextPosition = currentPosition - 1;
  }

  if (nextPosition > floorCount) {
    nextPosition = floorCount - 1;
    nextDirection = Usage.Down;
  }
  if (nextPosition < minFloor) {
    nextPosition = minFloor + 1;
    nextDirection = Usage.Up;
  }

  nextState.position = clampMockFloor(nextPosition, minFloor, floorCount);
  nextState.direction = nextDirection;
  applyMockUsageState(nextState.direction, nextState.doorStatus);

  return nextState;
}
